import maplibregl from 'maplibre-gl'
import { defineStore } from 'pinia'

import CONFIG from '@/services/configService'
import { connectivityService } from '@/services/connectivityService'
import { formatByteSize } from '@/services/formattingService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { throwIfOfflineReadOnly } from '@/services/offlineModificationGuardService'
import { processPhotoFromArrayBuffer } from '@/services/photoService'
import {
  matchPhotoCoordinatesFromTracks,
  PHOTO_TRACK_ENDPOINT_TOLERANCE_MS,
} from '@/services/photoTrackMatchingService'
import { syncToasts } from '@/services/syncCopy'
import { showError, showInfo } from '@/services/toastService'
import { useEventStore } from '@/stores/eventStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { EventType } from '@/types/event'
import type { EventChange } from '@/types/event'
import type { Photo } from '@/types/photo'
import type { Tour } from '@/types/tour'
import { requireItemEditable } from '@/utils/permissionDialog'

export interface PhotoState {
  photos: Photo[]
  isLoading: boolean
  error: string | null
  selectedPhotoId: string | null
  processingCount: number
  totalToProcess: number
  arePhotosVisible: boolean
  _isLoading: boolean
  currentPhotoProgress: number
  currentProcessingPhoto: string | null
}

function normalizeTimestamp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.length > 0) {
    const numericValue = Number(value)
    if (Number.isFinite(numericValue)) {
      return numericValue
    }

    const parsedValue = new Date(value).getTime()
    if (Number.isFinite(parsedValue)) {
      return parsedValue
    }
  }

  return null
}

function shouldLoadTourDetailForInterpolation(
  tour: Pick<Tour, 'clientId' | 'tracks' | '_detailsLoaded'>,
  takenAt: number,
  endpointToleranceMs: number,
): boolean {
  if (tour._detailsLoaded) {
    return false
  }

  return tour.tracks.some((track) => {
    const startTime = normalizeTimestamp(track.startTime)
    const endTime = normalizeTimestamp(track.endTime)

    if (startTime === null || endTime === null) {
      return false
    }

    const trackStartTime = Math.min(startTime, endTime)
    const trackEndTime = Math.max(startTime, endTime)
    const nearestTimeDistanceMs =
      takenAt < trackStartTime
        ? trackStartTime - takenAt
        : takenAt > trackEndTime
          ? takenAt - trackEndTime
          : 0

    if (nearestTimeDistanceMs > endpointToleranceMs) {
      return false
    }

    return true
  })
}

async function ensureCandidateTourDetailsForPhotoInterpolation(
  workspaceStore: ReturnType<typeof useWorkspaceStore>,
  takenAt: number,
): Promise<void> {
  const candidateTours = workspaceStore.tours.filter((tour) =>
    shouldLoadTourDetailForInterpolation(tour, takenAt, PHOTO_TRACK_ENDPOINT_TOLERANCE_MS),
  )

  if (candidateTours.length === 0) {
    return
  }

  for (const tour of candidateTours) {
    try {
      await workspaceStore.ensureTourDetailLoaded(tour.clientId)
    } catch (error) {
      void error
    }
  }
}

export const usePhotoStore = defineStore('photo', {
  state: (): PhotoState => ({
    photos: [],
    isLoading: false,
    error: null,
    selectedPhotoId: null,
    processingCount: 0,
    totalToProcess: 0,
    _isLoading: false,
    arePhotosVisible: true,
    currentPhotoProgress: 0,
    currentProcessingPhoto: null,
  }),

  getters: {
    allPhotos: (state) => state.photos,

    // Photos sorted by takenAt timestamp (oldest first, photos without takenAt go to end)
    photosSortedByDate: (state) => {
      return [...state.photos].sort((a, b) => {
        // Photos without takenAt go to the end
        if (!a.takenAt && !b.takenAt) return 0
        if (!a.takenAt) return 1
        if (!b.takenAt) return -1

        // Sort by takenAt timestamp (oldest first)
        return a.takenAt - b.takenAt
      })
    },

    photosWithCoordinates: (state) => state.photos.filter((photo: Photo) => photo.hasCoordinates),

    selectedPhoto: (state) =>
      state.photos.find((photo: Photo) => photo.clientId === state.selectedPhotoId) || null,

    hasError: (state) => state.error !== null,

    processingProgress: (state) => {
      if (state.totalToProcess === 0) return 0
      return Math.round((state.processingCount / state.totalToProcess) * 100)
    },

    isProcessingComplete: (state) =>
      state.processingCount === state.totalToProcess && state.totalToProcess > 0,
  },

  actions: {
    /**
     * Initialize the store and load data from IndexedDB
     */
    async init() {
      // Set loading flag to true to prevent subscription events during initialization
      this.isLoading = true
      try {
        // Photos are no longer persisted to IndexedDB
        // Data stays in memory only and is loaded from server on sync

        // Set up subscription for state changes AFTER initial data is loaded
        this.$subscribe((mutation) => {
          // Skip if we're still in the loading phase
          if (this.isLoading) return

          this._isLoading = true

          // Handle project property changes
          if (mutation.type === 'direct') {
            // Track what has changed
            let photosChanged = false

            // Check different ways mutations can be reported
            if (mutation.events && Array.isArray(mutation.events)) {
              photosChanged = mutation.events.includes('photos')
            } else if ('payload' in mutation) {
              const payload = mutation.payload as Record<string, unknown>
              photosChanged = 'photos' in payload
            }

            // Handle events for photo changes
            if (photosChanged) {
              // Handle photo change events if needed in the future
            }
          }
        })

        // Turn off loading flag AFTER subscription is set up and all data is loaded
        this._isLoading = true
        this.isLoading = false
      } catch (error) {
        showError(
          `Failed to initialize photo store: ${error instanceof Error ? error.message : String(error)}`,
        )
        this.error = error instanceof Error ? error.message : 'Failed to load photos'
        this.isLoading = false
        throw error
      }
    },

    async loadPhotoFiles(fileSource: FileList | File[]) {
      throwIfOfflineReadOnly('upload photos')

      // Photos belong to a collection. Without an active one the events would carry an
      // empty workspaceClientId — the server rejects those forever (the poison-event
      // incident, docs/concepts/sync-feedback.md §6). Refuse loudly instead.
      const workspaceStore = useWorkspaceStore()
      if (!workspaceStore.clientId) {
        logService.warn(LOG_CATEGORIES.FILE, 'Photo upload blocked: no active collection')
        showError(syncToasts.photosNeedCollection)
        return
      }

      this.isLoading = true
      this.error = null
      const eventStore = useEventStore()
      const files = Array.from(fileSource)

      // Reset processing counters
      this.processingCount = 0
      this.totalToProcess = 0
      this.currentPhotoProgress = 0
      this.currentProcessingPhoto = null

      // Count only photo files for the total
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const fileName = file.name.toLowerCase()
        if (
          file.type.startsWith('image/') ||
          fileName.endsWith('.heic') ||
          fileName.endsWith('.heif')
        ) {
          this.totalToProcess++
        }
      }

      try {
        const workspaceId = workspaceStore.clientId

        await eventStore.runInSyncBatch(async () => {
          // Process photos one by one using Web Worker
          for (let i = 0; i < files.length; i++) {
            const file = files[i]
            const fileName = file.name.toLowerCase()
            if (
              file.type.startsWith('image/') ||
              fileName.endsWith('.heic') ||
              fileName.endsWith('.heif')
            ) {
              // Check file size before processing
              if (file.size > CONFIG.UPLOAD_MAX_FILE_SIZE) {
                showError(
                  `Photo "${file.name}" exceeds the maximum size of ${formatByteSize(CONFIG.UPLOAD_MAX_FILE_SIZE)}. Skipping file.`,
                )
                continue
              }

              // Set current processing photo
              this.currentProcessingPhoto = file.name

              try {
                // Convert to ArrayBuffer for transferable processing
                const arrayBuffer = await file.arrayBuffer()

                // Process each photo using Web Worker with progress tracking
                const photoItem = await processPhotoFromArrayBuffer(
                  arrayBuffer,
                  file.name,
                  file,
                  (progress: number) => {
                    // Update individual photo progress (0-100)
                    this.currentPhotoProgress = progress
                  },
                )

                if (!photoItem) {
                  continue
                }

                if (
                  !photoItem.hasCoordinates &&
                  typeof photoItem.takenAt === 'number' &&
                  Number.isFinite(photoItem.takenAt)
                ) {
                  await ensureCandidateTourDetailsForPhotoInterpolation(
                    workspaceStore,
                    photoItem.takenAt,
                  )

                  const matchedCoordinates = matchPhotoCoordinatesFromTracks(
                    workspaceStore.tours,
                    photoItem.takenAt,
                  )

                  if (matchedCoordinates) {
                    photoItem.lat = matchedCoordinates.lat
                    photoItem.lon = matchedCoordinates.lon
                    photoItem.hasCoordinates = true
                    photoItem.coordinateSource = matchedCoordinates.source
                  }
                }

                // Create a PHOTO_CREATED event first (includes quota check)
                try {
                  await eventStore.addEvent({
                    type: EventType.PHOTO_CREATED,
                    payload: {
                      workspaceClientId: workspaceId,
                      ...photoItem,
                    },
                  })

                  // Only add to UI state if event creation succeeded (quota check passed)
                  this.photos.push(photoItem)
                  // No persistence to IndexedDB - data stays in memory only
                  this.processingCount++
                } catch (error) {
                  // Quota check cancelled: skip the photo, but never silently — the user must
                  // know it was NOT saved (docs/concepts/sync-feedback.md §10.2).
                  if (error instanceof Error && error.message === 'QUOTA_CHECK_CANCELLED') {
                    logService.warn(LOG_CATEGORIES.FILE, 'Photo skipped: quota check cancelled', {
                      fileName: file.name,
                    })
                    showError(`"${file.name}": ${syncToasts.photoNotSaved}`)
                    continue
                  }
                  // Re-throw other errors
                  throw error
                }
              } catch (error) {
                showError(
                  `Failed to process photo "${file.name}": ${error instanceof Error ? error.message : String(error)}`,
                )
                // Continue with next photo instead of failing entire batch
                continue
              } finally {
                // Reset progress for next photo
                this.currentPhotoProgress = 0
                this.currentProcessingPhoto = null
              }
            }
          }
        })
        // Confirm offline adds once per batch: the work is saved locally and will sync later
        // (docs/concepts/sync-feedback.md §10.2).
        if (this.processingCount > 0 && !connectivityService.isEffectivelyOnline.value) {
          showInfo(syncToasts.savedOffline)
        }
      } catch (err) {
        // Quota cancellation skips the batch — loudly, never silently (§10.2)
        if (err instanceof Error && err.message === 'QUOTA_CHECK_CANCELLED') {
          logService.warn(LOG_CATEGORIES.FILE, 'Photo batch skipped: quota check cancelled')
          showError(syncToasts.photoNotSaved)
          return
        }
        showError(`Error processing photos: ${err instanceof Error ? err.message : String(err)}`)
        this.error = err instanceof Error ? err.message : 'Failed to process photos'
        throw err
      } finally {
        this.isLoading = false
      }
    },

    // Enhanced versions of existing methods that emit events
    selectPhoto(photoId: string) {
      this.selectedPhotoId = photoId
    },

    async clearPhoto(photoId: string) {
      throwIfOfflineReadOnly('delete photo')

      const eventStore = useEventStore()
      const photoToRemove = this.photos.find((img: Photo) => img.clientId === photoId)

      if (photoToRemove) {
        if (
          !(await requireItemEditable(photoToRemove.canEdit, 'photo', photoToRemove.uploadedByName))
        )
          return
        // Mark photo as pending deletion
        photoToRemove._pendingDeletion = true
        // No persistence to IndexedDB - state stays in memory only

        try {
          // Send the DELETE event
          await eventStore.addEvent({
            type: EventType.PHOTO_DELETED,
            payload: {
              clientId: photoId,
            },
          })

          // After successful event, mark as deleted
          photoToRemove._pendingDeletion = true
          // No persistence to IndexedDB - state stays in memory only

          // Now remove from the array and database
          this.photos = this.photos.filter((img: Photo) => img.clientId !== photoId)

          if (this.selectedPhotoId === photoId) {
            this.selectedPhotoId = null
          }

          // If the deleted photo was the cover, clear it immediately
          const workspaceStore = useWorkspaceStore()
          if (workspaceStore.coverPhotoClientId === photoId) {
            workspaceStore.coverPhotoClientId = null
            workspaceStore.coverPhotoUrl = null
            workspaceStore.coverPhotoThumbnailUrl = null
          }
        } catch (error) {
          // In case of error, revert the pending deletion flag
          photoToRemove._pendingDeletion = false
          // No persistence to IndexedDB - state stays in memory only
          throw error
        }
      }
    },

    async clearAllPhotos() {
      throwIfOfflineReadOnly('delete all photos')

      const eventStore = useEventStore()

      await eventStore.runInSyncBatch(async () => {
        for (const photo of [...this.photos]) {
          await this.clearPhoto(photo.clientId)
        }
      })
    },

    async updatePhoto(photoId: string, updates: Partial<Photo>) {
      throwIfOfflineReadOnly('edit photo')

      const photo = this.photos.find((photo: Photo) => photo.clientId === photoId)
      const eventStore = useEventStore()

      if (photo) {
        if (!(await requireItemEditable(photo.canEdit, 'photo', photo.uploadedByName))) return

        // Create a PHOTO_UPDATED event with changes
        const changes: EventChange[] = []

        // Add each field to changes, excluding 'clientId'
        Object.entries(updates).forEach(([field, value]) => {
          if (field !== 'clientId') {
            changes.push({ field, value })
          }
        })

        if (changes.length > 0) {
          eventStore.addEvent({
            type: EventType.PHOTO_UPDATED,
            payload: {
              clientId: photoId,
              changes,
            },
          })
        }

        // Apply updates
        Object.assign(photo, updates)

        // No persistence to IndexedDB - data stays in memory only
      }
    },

    // Calculate map bounds for photos with coordinates
    calculateMapBounds() {
      // Create bounds from all photos with coordinates
      const bounds = new maplibregl.LngLatBounds()
      let hasPoints = false

      this.photos.forEach((photo) => {
        if (photo.hasCoordinates && photo.lat && photo.lon) {
          bounds.extend([photo.lon, photo.lat])
          hasPoints = true
        }
      })

      return hasPoints ? bounds : null
    },

    // Generate thumbnail from a photo's data
    async generateThumbnail(photo: Photo) {
      try {
        // Create a thumbnail from either the File object or data URL
        const img = new Image()

        // When the image loads, create a thumbnail from it
        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            const canvas = document.createElement('canvas')
            const pixelRatio = window.devicePixelRatio || 1
            const max_size = 120

            let width = img.width
            let height = img.height

            if (width > height) {
              if (width > max_size) {
                height = Math.round(height * (max_size / width))
                width = max_size
              }
            } else {
              if (height > max_size) {
                width = Math.round(width * (max_size / height))
                height = max_size
              }
            }

            width = Math.floor(width)
            height = Math.floor(height)

            canvas.width = Math.floor(width * pixelRatio)
            canvas.height = Math.floor(height * pixelRatio)

            const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: true })
            if (ctx) {
              ctx.imageSmoothingEnabled = true
              ctx.imageSmoothingQuality = 'high'

              ctx.scale(pixelRatio, pixelRatio)
              ctx.drawImage(img, 0, 0, width, height)

              photo._thumbnailData = canvas.toDataURL('image/png')
              resolve()
            } else {
              reject(new Error('Could not get canvas context'))
            }
          }

          img.onerror = () => {
            reject(new Error('Failed to load image'))
          }

          // Set the source to the file data URL or create one from the File object
          if (photo._binaryDataUrl) {
            img.src = photo._binaryDataUrl
          } else if (photo.binaryData instanceof File) {
            const objectUrl = URL.createObjectURL(photo.binaryData)
            img.src = objectUrl
            // We'll need to clean up the URL after the image loads
            img.onload = () => {
              const canvas = document.createElement('canvas')
              const pixelRatio = window.devicePixelRatio || 1
              const max_size = 120

              let width = img.width
              let height = img.height

              if (width > height) {
                if (width > max_size) {
                  height = Math.round(height * (max_size / width))
                  width = max_size
                }
              } else {
                if (height > max_size) {
                  width = Math.round(width * (max_size / height))
                  height = max_size
                }
              }

              width = Math.floor(width)
              height = Math.floor(height)

              canvas.width = Math.floor(width * pixelRatio)
              canvas.height = Math.floor(height * pixelRatio)

              const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: true })
              if (ctx) {
                ctx.imageSmoothingEnabled = true
                ctx.imageSmoothingQuality = 'high'

                ctx.scale(pixelRatio, pixelRatio)
                ctx.drawImage(img, 0, 0, width, height)

                photo._thumbnailData = canvas.toDataURL('image/png')

                // Clean up the URL
                URL.revokeObjectURL(objectUrl)
                resolve()
              } else {
                URL.revokeObjectURL(objectUrl)
                reject(new Error('Could not get canvas context'))
              }
            }
          } else {
            reject(new Error('No image data available to generate thumbnail'))
          }
        })

        // No persistence to IndexedDB - data stays in memory only
      } catch (error) {
        showError(
          `Error generating thumbnail: ${error instanceof Error ? error.message : String(error)}`,
        )
        throw error
      }
    },
  },
})
